import { Check, Copy, Link as LinkIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

// Public-facing share domain. When the user actually points DNS for this
// domain to the deployment, share links become live. Until then it's a clean
// display string everyone can copy. Falls back to a sensible default if the
// env var is not set so we never render an empty link.
const PUBLIC_DOMAIN = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_PUBLIC_DOMAIN) || '1simplecycler3.com';

const ReferralLinkBox = ({ referralCode, label = 'Your Personal Referral Link' }) => {
  const [copied, setCopied] = useState(false);
  const link = useMemo(() => {
    const handle = (referralCode || '').trim();
    return handle ? `${PUBLIC_DOMAIN}/${handle}` : PUBLIC_DOMAIN;
  }, [referralCode]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Referral link copied');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Copy failed — select the text manually');
    }
  };

  return (
    <div className="sc-glass-strong rounded-2xl p-5 md:p-6 relative overflow-hidden" data-testid="referral-link-box">
      <div
        className="absolute -top-16 -right-16 w-44 h-44 pointer-events-none rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(255,182,18,0.22), transparent 60%)',
        }}
      />
      <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-4">
        <div
          className="w-11 h-11 shrink-0 rounded-xl flex items-center justify-center border"
          style={{
            background: 'rgba(255, 182, 18, 0.10)',
            borderColor: 'rgba(255, 182, 18, 0.32)',
          }}
        >
          <LinkIcon className="w-5 h-5" style={{ color: '#FFC20E' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">{label}</div>
          <div className="sc-tabular font-display font-semibold text-base md:text-[17px] truncate mt-1" title={link} data-testid="referral-link-value">
            {link}
          </div>
        </div>
        <button type="button" onClick={copy} className="sc-btn-gold shrink-0" style={{ padding: '12px 18px', fontSize: 13 }} data-testid="referral-link-copy">
          {copied ? (
            <>
              <Check className="w-4 h-4" /> Copied
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" /> Copy
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ReferralLinkBox;
